const STORAGE_KEY = 'cadran.onboarding';

export interface Proche {
  prenom: string;
  contact: string;
  role: 'proche' | 'co-admin';
}

export interface OnboardingState {
  step: 2 | 3 | 4 | 5 | 6 | 'success';
  foyerNom: string;
  seniorPrenom: string;
  relation: string;
  pairingDone: boolean;
  proches: Proche[];
  messageMode: 'mot' | 'photo';
  messageContenu: string;
  plan: 'tablette' | 'famille' | 'presence';
  done: boolean;
}

export const INITIAL_STATE: OnboardingState = {
  step: 2,
  foyerNom: '',
  seniorPrenom: '',
  relation: '',
  pairingDone: false,
  proches: [],
  messageMode: 'mot',
  messageContenu: '',
  plan: 'famille',
  done: false,
};

export type Action =
  | { type: 'SET_FOYER'; foyerNom: string; seniorPrenom: string; relation: string }
  | { type: 'PAIRING_DONE' }
  | { type: 'SET_PROCHES'; proches: Proche[] }
  | { type: 'SET_MESSAGE'; mode: 'mot' | 'photo'; contenu: string }
  | { type: 'SET_PLAN'; plan: OnboardingState['plan'] }
  | { type: 'NEXT' }
  | { type: 'BACK' }
  | { type: 'COMPLETE' }
  | { type: 'RESTART' };

export function reducer(state: OnboardingState, action: Action): OnboardingState {
  switch (action.type) {
    case 'SET_FOYER':
      return {
        ...state,
        foyerNom: action.foyerNom,
        seniorPrenom: action.seniorPrenom,
        relation: action.relation,
      };
    case 'PAIRING_DONE':
      return { ...state, pairingDone: true };
    case 'SET_PROCHES':
      return { ...state, proches: action.proches };
    case 'SET_MESSAGE':
      return { ...state, messageMode: action.mode, messageContenu: action.contenu };
    case 'SET_PLAN':
      return { ...state, plan: action.plan };
    case 'NEXT': {
      if (state.step === 'success') return state;
      const next = state.step < 6 ? ((state.step + 1) as OnboardingState['step']) : 'success';
      return { ...state, step: next };
    }
    case 'BACK': {
      if (state.step === 2 || state.step === 'success') return state;
      return { ...state, step: (state.step - 1) as OnboardingState['step'] };
    }
    case 'COMPLETE':
      return { ...state, step: 'success', done: true };
    case 'RESTART':
      return INITIAL_STATE;
    default:
      return state;
  }
}

export function loadState(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw)
      return {
        ...INITIAL_STATE,
        seniorPrenom: localStorage.getItem('cadran.prenom') ?? '',
      };
    return { ...INITIAL_STATE, ...JSON.parse(raw) };
    // SAFE: localStorage absent (SSR/private) ou JSON corrompu → état initial propre
  } catch {
    return INITIAL_STATE;
  }
}

export function saveState(state: OnboardingState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    // SAFE: quota localStorage dépassé — la persistance est optionnelle
  } catch {
    // quota dépassé — l'état en mémoire reste valide
  }
}

export function clearState(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('cadran.role');
  localStorage.removeItem('cadran.prenom');
  localStorage.removeItem('cadran.email');
}

/** Hash déterministe 6 chiffres à partir du nom de foyer. Format XXX·XXX */
export function pairingCode(foyerNom: string): string {
  let h = 0;
  for (let i = 0; i < foyerNom.length; i++) {
    h = (Math.imul(31, h) + foyerNom.charCodeAt(i)) | 0;
  }
  const n = Math.abs(h) % 1_000_000;
  const s = String(n).padStart(6, '0');
  return `${s.slice(0, 3)} · ${s.slice(3)}`;
}
